/**
 * useShareStore Hook
 *
 * Creates and manages a standalone ShareStore instance for public share pages.
 * Not connected to RootStore since public pages have no auth context.
 *
 * Features:
 * - Creates store instance on mount
 * - Loads share data when token changes
 * - Disposes store on unmount (blob URL cleanup)
 *
 * @example
 * ```tsx
 * function SharePage() {
 *   const { token } = useParams<{ token: string }>();
 *   const store = useShareStore(token);
 *
 *   if (store.isLoading) return <Loading />;
 *   if (store.error) return <Error message={store.error} />;
 *
 *   return <ShareContent store={store} />;
 * }
 * ```
 */

import { useRef, useEffect } from "react";
import { ShareStore, type ShareStoreInstance } from "@/stores/shares";

/**
 * Create and manage a ShareStore instance for public share pages.
 *
 * @param token - Share token from URL params
 * @returns ShareStore instance
 */
export function useShareStore(token: string | undefined): ShareStoreInstance {
  // Create store once and keep same reference
  const storeRef = useRef<ShareStoreInstance | null>(null);

  if (!storeRef.current) {
    storeRef.current = ShareStore.create({});
  }

  const store = storeRef.current;

  // Load share when token is available/changes
  useEffect(() => {
    if (token) {
      store.loadShare(token);
    } else {
      // No token - set error state directly via store action
      // We can't set error directly since it's an MST model
      // But loadShare handles the "no token" case already
    }
  }, [token, store]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      store.dispose();
    };
  }, [store]);

  return store;
}
